import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';

const RGRL_BASE_URL = 'vinculacion-rgrl';
const VIISITS_BASE_URL = 'visitas-rgrl';

export interface GenerateFilesBody {
    idsPresentacion: number[]
}

export interface GenerateVisitsFilesBody {
    idsVisita: number[]
}


export type SRT_RESPONSE_RGRL_TXT_TYPE = 'RB' | 'RC' | 'RH' | 'RI' | 'RM' | 'RO' | 'RS'
export type SRT_RESPONSE_VISITS_TXT_TYPE = 'VI' | 'RD'

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});
 
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const { auth }: any = api.getState();

        const refreshResult = await baseQuery(
            {
                url: '/auth/refresh-token',
                method: 'POST',
                credentials: 'include',
                body: { username: auth?.user?.usuario }
            },
            api,
            extraOptions
        );
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const rgrlVinculationApi = createApi({
    reducerPath: 'rgrlVinculationApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['VincluationRGRLPresentations', 'Contracts', 'Establishments', 'Visits'],
    endpoints: (builder) => ({
        getRGRLVinculationPresentations: builder.query({
            query: (params) => {
                return {
                    url: `${RGRL_BASE_URL}/presentaciones`,
                    params: params
                };
            },
            providesTags: ['VincluationRGRLPresentations']
        }),
        getRGRLVinculationContracts: builder.query({
            query: ({idOrganizacion}) => ({
                url: `${RGRL_BASE_URL}/contratos`,
                params: {
                    ...(idOrganizacion?.id ?
                        { idOrganizacion: idOrganizacion.id } :
                        idOrganizacion ? { idOrganizacion } :
                            {}
                    )
                }
            }),
            providesTags: ['Contracts'],
            transformResponse: (response: any) => response?.data?.contratos
        }),
        getRGRLVinculationEstablishments: builder.query({
            query: ({ idContrato }) => ({
                url: `${RGRL_BASE_URL}/establecimientos`,
                params: {
                    ...(idContrato ? { idOrganismoCentral: idContrato?.id } : {}),
                }
            }),
            providesTags: ['Establishments'],
            transformResponse: (response: any) => response?.data?.establecimientos
        }),
        generateFiles: builder.mutation({
            query: (body: GenerateFilesBody) => ({
                url: `${RGRL_BASE_URL}/archivos`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['VincluationRGRLPresentations']
        }),
        uploadResponseSRT: builder.mutation({
            query: ({ nameType, body }: { nameType: SRT_RESPONSE_RGRL_TXT_TYPE; body: any }) => ({
                url: `${RGRL_BASE_URL}/archivos/${nameType}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['VincluationRGRLPresentations']
        }),
        //VISITS ENDPOINTS
        getVisitsSRT: builder.query({
            query: (params) => {
                return {
                    url: `${VIISITS_BASE_URL}`,
                    params: {
                        ...params,
                        ...(
                            params?.idOrganizacion?.id ?
                            { idOrganizacion: params.idOrganizacion?.id } :
                            params?.idOrganizacion ?
                                { idOrganizacion: params.idOrganizacion } :
                                {}
                        )
                    }
                };
            },
            providesTags: ['Visits']
        }),
        generateVisitFilesSRT: builder.mutation({
            query: (body: GenerateVisitsFilesBody) => ({
                url: `${VIISITS_BASE_URL}/archivos`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Visits']
        }),
        uploadVisitsResponseSRT: builder.mutation({
            query: ({ nameType, body }: { nameType: SRT_RESPONSE_VISITS_TXT_TYPE; body: any }) => ({
                url: `${VIISITS_BASE_URL}/archivos/${nameType}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Visits']
        })
    })
});

export const {
    useGetRGRLVinculationPresentationsQuery,
    useGetRGRLVinculationContractsQuery,
    useGetRGRLVinculationEstablishmentsQuery,
    useGenerateFilesMutation,
    useUploadResponseSRTMutation,
    //VISITS ENDPOINTS
    useGetVisitsSRTQuery,
    useGenerateVisitFilesSRTMutation,
    useUploadVisitsResponseSRTMutation
} = rgrlVinculationApi;
